'use client';

import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { usePathname, useSearchParams } from 'next/navigation';
import qs from 'qs';
import { useState } from 'react';
import type { Bookmark } from '@/types/bookmarks';
import type { User } from '@/types/user';
import { factory } from '@/utils/queryFactory';
import BookmarkTrigger from './modules/BookmarkTrigger';
import { Content } from './modules/Content';
import { DrawerWrapper } from './modules/Drawer';
import { PopoverWrapper } from './modules/PopoverWrapper';
import type { Routes } from './utils/parseRouteName';

const findActiveBookmark = (array: Bookmark[], findFunc: (d: Bookmark) => boolean): [Bookmark | null, Bookmark[]] => {
  const index = array.findIndex(findFunc);
  if (index === -1) return [null, array];

  const newArray = [...array.slice(0, index), ...array.slice(index + 1)];

  return [array[index], newArray];
};

const getBookmarksForSection = (d: Bookmark, section: Routes | null) =>
  d.currentRouteName.split('.')[0] === section || section === null;

export const Bookmarks = ({ bookmarks, user }: { bookmarks: Bookmark[]; user: User }) => {
  const pathname = usePathname();
  const [section, setSection] = useState<Routes | null>(null);

  const query = useQuery({
    ...factory.bookmarks.byUserId({ userId: user.id }),
    initialData: bookmarks,
  });

  const searchParams = useSearchParams();
  const currentParams = qs.parse(searchParams.toString());
  const [activeBookmark, notActiveBookmarks] = findActiveBookmark(query.data, (bookmark) => {
    const [route, bookmarkQuery] = bookmark.currentURL.split('?');
    if (!route) return false;
    const isActive = route === pathname && _.isEqual(currentParams, qs.parse(bookmarkQuery ?? ''));
    return isActive;
  });

  const sectionBookmarks = notActiveBookmarks.filter((bookmark) => getBookmarksForSection(bookmark, section));

  return (
    <>
      <div className='tablet-lg:hidden'>
        <PopoverWrapper
          activeBookmark={activeBookmark}
          dataLength={query.data.length}
        >
          <Content
            activeBookmark={activeBookmark}
            bookmarks={sectionBookmarks}
            section={section}
            setSection={setSection}
          />
        </PopoverWrapper>
      </div>
      <div className='tablet-lg:block hidden'>
        <DrawerWrapper
          trigger={
            <BookmarkTrigger
              activeBookmark={activeBookmark}
              dataLength={query.data.length}
            />
          }
          title='Dina bokmärken'
        >
          <Content
            activeBookmark={activeBookmark}
            bookmarks={sectionBookmarks}
            section={section}
            setSection={setSection}
          />
        </DrawerWrapper>
      </div>
    </>
  );
};
